<template>
  <div>
    <a-spin :spinning="loading">
      <h2 v-if="!form.id">Create Sales Channel Promotion</h2>
      <h2 v-else-if="form.id">Edit Sales Channel Promotion - {{form.name}}</h2>

      <hr>
      <div class="row no-gutters">
        <div class="col-md-2" style="border-right: 1px solid rgb(224, 224, 224);">
          <a-steps direction="vertical" :current="current">
            <a-step title="Info"/>
            <a-step title="Sales Channels"/>
            <a-step title="Promotion Logic"/>
            <a-step title="Settings"/>
            <a-step title="Summary"/>
          </a-steps>
        </div>
        <div class="col-md-7">
          <div class="card mb-3 border-0" v-if="!loading">
            <!-- info -->
            <div v-if="current == 0" class="card-body py-0">
              <h2 class="card-title mb-2">Promo Information</h2>
              <div class="row ml-2">
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Name of Promotion</h4>
                  <a-input label="Promo Name" v-model="form.name" input-classes="form-control" style="margin-bottom: 0px;" placeholder="Enter a description for your promo" required :disabled="viewOnly" />
                  <small>Example: 10% Off All Shirts</small>
                </div>
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Short Code for Promotion</h4>
                  <a-input label="Promo Code" v-model="form.code" input-classes="form-control" style="margin-bottom: 0px;" placeholder="Enter a short code to identify your promo" required :disabled="viewOnly || updateOnly" />
                  <small>Example: PROMO10% (no spaces); This will also serve as the discount code for voucher promotions</small>
                </div>
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Type of Application</h4>
                  <base-radio class="mb-2" name="automatic" value="automatic" v-model="form.application_type" :disabled="viewOnly || updateOnly">
                    Automatic Discount
                    <br><small>Discounts will automatically be applied to the order.</small>
                  </base-radio>
                  <base-radio class="mb-2" name="voucher" value="voucher" v-model="form.application_type" :disabled="viewOnly || updateOnly">
                    Voucher / Discount Code
                    <br><small>Customers will get a discount if they enter a code at checkout.</small>
                  </base-radio>
                </div>
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Type of Promotion</h4>
                  <div v-if="form.application_type === 'automatic'">
                    <h5 class="card-title mt-3">Basic Promotions</h5>
                    <base-radio class="mb-2" name="automatic_percentage_discount" value="automatic_percentage_discount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Percentage Discount
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_fixed_amount_discount" value="automatic_fixed_amount_discount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Fixed Amount Discount
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_free_shipping" value="automatic_free_shipping" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Free Shipping
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_discounted_amount" value="automatic_discounted_amount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Discounted Amount
                    </base-radio>
                    <h5 class="card-title mt-3">Advance/Bundle Promotions</h5>
                    <base-radio class="mb-2" name="automatic_buy_get" value="automatic_buy_get" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Bundle - But X Get Y
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_flexi_combo" value="automatic_flexi_combo" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Flexi Combo
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_bundle_deals" value="automatic_bundle_deals" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Bundle Deals
                    </base-radio>
                    <base-radio class="mb-2" name="automatic_add_on_deals" value="automatic_add_on_deals" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Add-On Deals
                    </base-radio>
                  </div>
                  <div v-else>
                    <h5 class="card-title mt-3">Voucher & Discount Codes</h5>
                    <base-radio class="mb-2" name="voucher_percentage_discount" value="voucher_percentage_discount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Percentage Discount
                    </base-radio>
                    <base-radio class="mb-2" name="voucher_fixed_amount_discount" value="voucher_fixed_amount_discount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Fixed Amount Discount
                    </base-radio>
                    <base-radio class="mb-2" name="voucher_free_shipping" value="voucher_free_shipping" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Free Shipping
                    </base-radio>
                    <base-radio class="mb-2" name="voucher_discounted_amount" value="voucher_discounted_amount" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Discounted Amount
                    </base-radio>
                    <base-radio class="mb-2" name="voucher_buy_get" value="voucher_buy_get" v-model="form.sales_channel_promo_logic" :disabled="viewOnly || updateOnly">
                      Bundle - But X Get Y
                    </base-radio>
                  </div>
                  <br>
                </div>
              </div>
            </div>
            <!-- sales channels -->
            <div v-else-if="current == 1" class="card-body py-0">
              <h2 class="card-title mb-2">Sales Channels</h2>
              <div class="row ml-2">
                <div class="col-md-12 pt-4 pb-2">
                  <p>Promotions will apply to the following sales channels:</p>
                  <span class="d-none">{{form.sales_channel_stores}}</span>
                </div>
                <div class="col-md-12 pb-4" v-for="salesChannel in availableSalesChannels().filter(r => displaySalesChannel(r.code))" v-bind:key="salesChannel.code">
                  <h5 class="card-title mb-2">{{salesChannel.name}}</h5>
                  <div v-for="salesChannelAccount in getSalesChannelAccounts(salesChannel.code)" v-bind:key="salesChannelAccount.id">
                    <base-checkbox class="mt-2" :value="true" v-model="form.sales_channel_stores[salesChannelAccount.id]" :disabled="viewOnly || !form.from_bxi">
                      {{salesChannelAccount.name}}
                    </base-checkbox>
                  </div>
                </div>
              </div>
            </div>
            <!-- promotion logic -->
            <div v-else-if="current == 2" class="card-body py-0">
              <h2 class="card-title mb-2">Promotion Logic</h2>
              <percentage-discount
                v-if="form.sales_channel_promo_logic.includes('percentage_discount')"
                :form="form"
                :promotion="promotion"
                :viewOnly="viewOnly"
                :storeTypes="storeTypes"
              ></percentage-discount>
              <fixed-amount-discount
                v-if="form.sales_channel_promo_logic.includes('fixed_amount_discount')"
                :form="form"
                :promotion="promotion"
                :viewOnly="viewOnly"
                :storeTypes="storeTypes"
              ></fixed-amount-discount>
              <discounted-amount
                v-if="form.sales_channel_promo_logic.includes('discounted_amount')"
                :form="form"
                :promotion="promotion"
                :viewOnly="viewOnly"
                :storeTypes="storeTypes"
              ></discounted-amount>
              <free-shipping
                v-if="form.sales_channel_promo_logic.includes('free_shipping')"
                :form="form"
                :promotion="promotion"
                :viewOnly="viewOnly"
                :storeTypes="storeTypes"
              ></free-shipping>
              <buy-get-discount
                v-if="form.sales_channel_promo_logic.includes('buy_get')"
                :form="form"
                :promotion="promotion"
                :viewOnly="viewOnly"
                :storeTypes="storeTypes"
              ></buy-get-discount>
            </div>
            <!-- settings -->
            <div v-else-if="current == 3" class="card-body py-0">
              <h2 class="card-title mb-2">Settings</h2>
              <div class="row ml-2">
                <div class="col-12 pt-4">
                  <h4 class="card-title mb-2">Promotion period</h4>
                  <div class="row m-0 p-0" style="margin-bottom: 5px;">
                    <div class="col-md-6 px-0 pr-1">
                      <a-date-picker v-model="form.data.starts_at" show-time format="MM/DD/YYYY hh:mm:ss a" style="width: 100%" placeholder="Start date"/>
                    </div>
                    <div class="col-md-6 px-0 pr-1">
                      <a-date-picker v-model="form.data.ends_at" show-time format="MM/DD/YYYY hh:mm:ss a" style="width: 100%" placeholder="End date"/>
                    </div>
                  </div>
                  <small>The date and time when the promotion is in effect.</small>
                </div>
                <div class="col-md-12 pt-3" v-if="form.promo_status === 'active' && storeTypes['shopify']">
                  <base-checkbox class="mb-2" :value="true" v-model="form.data._ends_at">
                    Include end date
                    <tooltip :message="'Only applies to Shopify'"></tooltip>
                    <br><small>If unchecked, promotion will not have an end date</small>
                  </base-checkbox>
                </div>
                <div class="col-md-12 pt-3" v-if="storeTypes['lazada'] && form.data.collect_start">
                  <h4 class="card-title mb-2">Collection period</h4>
                  <div class="row m-0 p-0" style="margin-bottom: 5px;">
                    <div class="col px-0 pr-1">
                      <a-date-picker v-model="form.data.collect_start" show-time format="MM/DD/YYYY hh:mm:ss a" style="width: 100%" placeholder="Start date"/>
                    </div>
                    <!-- <div class="col-md-6 px-0 pr-1">
                      <a-date-picker v-model="form.data.collect_end" show-time format="MM/DD/YYYY hh:mm:ss a" style="width: 100%" placeholder="End date"/>
                    </div> -->
                  </div>
                </div>
                <div class="col-md-12 pt-3" v-if="storeTypes['lazada'] && form.data.period_type">
                  <h4 class="card-title mb-2">Period Type</h4>
                  <base-radio class="mb-2" name="LONG_TERM" value="LONG_TERM" v-model="form.data.period_type">
                    Long Term
                    <br><small>Promotion will disregard the set start and end dates</small>
                  </base-radio>
                  <base-radio class="mb-2" name="SPECIAL_PERIOD" value="SPECIAL_PERIOD" v-model="form.data.period_type">
                    Special Period
                    <br><small>Promotion validity will follow the set start and end dates</small>
                  </base-radio>
                </div>
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Promo Status</h4>
                  <base-radio class="mb-2" name="active" value="active" v-model="form.promo_status">
                    Active
                  </base-radio>
                  <base-radio class="mb-2" name="inactive" value="inactive" v-model="form.promo_status">
                    Inactive
                  </base-radio>
                </div>
                <div class="col-md-12 pt-4">
                  <h4 class="card-title mb-2">Export Status</h4>
                  <base-radio class="mb-2" name="saved" value="active" v-model="form.export_status">
                    Saved
                    <br><small>Promotion will only be stored on BXI but will not be synced with the selected sales channels.</small>
                  </base-radio>
                  <base-radio class="mb-2" name="pending" value="active" v-model="form.export_status">
                    Pending
                    <br><small>Sync promotion with the selected sales channels immediately.</small>
                  </base-radio>
                </div>
              </div>
            </div>
            <!-- summary -->
            <div v-else-if="current == 4" class="card-body py-0">
              <h2 class="card-title mb-2">Summary</h2>
              <div class="row ml-2">
                <div class="col-12 pt-4">
                  <promotion-data-summary
                    :form="form"
                    :current="current"
                    :salesChannelAccounts="salesChannelAccounts"
                    :salesChannels="salesChannels"
                  ></promotion-data-summary>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 px-3 pr-5" v-if="[1,2,3].includes(current)">
          <div class="card" style="position: sticky; top: 60px;">
            <h4 class="card-header font-weight-bold border-bottom-0 pb-0">
              Summary
            </h4>
            <div class="card-body px-4 mx-2">
              <promotion-data-summary
                :form="form"
                :current="current"
                :salesChannelAccounts="salesChannelAccounts"
                :salesChannels="salesChannels"
              ></promotion-data-summary>
            </div>
          </div>
        </div>
      </div>
    </a-spin>
    <div :style="{ position: 'absolute', right: 0, bottom: 0, width: '100%', borderTop: '1px solid #e9e9e9', padding: '10px 16px', background: '#fff', textAlign: 'right', zIndex: 1}">
        <a-button v-if="current == 0" type="default" @click="cancel">CANCEL</a-button>
        <a-button v-if="current > 0" type="default" @click="backPage()">BACK</a-button>
        <a-button v-if="current < 4" class="ml-3" type="primary" @click="nextPage()">NEXT</a-button>
        <a-button v-if="current == 4" class="ml-3" type="primary" :loading="doneLoading" @click="submitPromo">DONE</a-button>
    </div>
  </div>
</template>

<script>
    import { mapState } from 'vuex'
    import HTTPRequest from '../../controllers/httpRequest.js'
    import Tooltip from './SalesChannelPromotionForms/Tooltip'
    import PromotionDataSummary from './SalesChannelPromotionForms/PromotionDataSummary'
    import PercentageDiscount from './SalesChannelPromotionForms/PercentageDiscount'
    import FixedAmountDiscount from './SalesChannelPromotionForms/FixedAmountDiscount'
    import DiscountedAmount from './SalesChannelPromotionForms/DiscountedAmount'
    import FreeShipping from './SalesChannelPromotionForms/FreeShipping'
    import BuyGetDiscount from './SalesChannelPromotionForms/BuyGetDiscount'

    const sleep = function(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    }
    const initialData = () => ({
      promotion: {},
      form: {
        id: null,
        name: null,
        code: null,
        application_type: 'automatic',
        sales_channel_stores: {},
        sales_channel_promo_logic: 'automatic_percentage_discount',
        data: {},
        from_bxi: true,
        promo_status: 'active',
        export_status: 'saved'
      },
      errors: [],
      products: [],
      loading: false,
      current: 0,
      doneLoading: false,
      salesChannels: [
        {
          name: 'BXI Online Store',
          code: 'online_store'
        },
        {
          name: 'Shopify Ecommerce',
          code: 'shopify'
        },
        {
          name: 'Lazada Marketplace',
          code: 'lazada'
        },
        {
          name: 'Shopee Marketplace',
          code: 'shopee'
        }
      ],
      salesChannelAccounts: [],
      initial: false
    })

    export default {
      components: {
        Tooltip,
        PromotionDataSummary,
        PercentageDiscount,
        FixedAmountDiscount,
        DiscountedAmount,
        FreeShipping,
        BuyGetDiscount
      },
      props: {
        promotionId: Number,
        viewOnly: Boolean,
        mode: String
      },
      computed: {
         ...mapState(['endPoint']),
         updateOnly: function(){
           return this.mode === 'update'
         },
         storeTypes: function(){
           return {
             online_store: this.includesStore('online_store'),
             shopify: this.includesStore('shopify'),
             lazada: this.includesStore('lazada'),
             shopee: this.includesStore('shopeey')
           }
         }
      },
      data: function () {
        return initialData()
      },
      methods: {
        resetData: function(){
          const data = initialData()
          Object.keys(data).forEach(k => this[k] = data[k])
          this.initial = this.mode === "update"
          this.getSalesChannels()
          if(this.promotionId){
            this.getPromotion()
          }
        },
        openNotificationWithIcon(type, description) {
          this.$notification[type]({
            message: 'Error',
            description: description
          });
        },
        cancel: function(){
          this.$emit('closeDrawer');
        },
        async nextPage(){
          this.errors = []
          switch (this.current) {
            case 0:
              if(!this.form.name || this.form.name.trim() === '')
                this.errors.push('Promotion name cannot be empty')
              if(!this.form.code || this.form.code.trim() === '')
                this.errors.push('Promotion code cannot be empty')
              break;
            case 1:
              this.validateSalesChannelStores(this.mode === 'create')
              if(Object.keys(this.form.sales_channel_stores).length === 0)
                this.errors.push('Select atleast one sales channel account')
              break;
            case 2:
              this.errors = this.form.data.validationErrors
              break;
            case 3:
              let starts_at = this.form.starts_at ? new Date(this.form.starts_at) : false
              let ends_at = this.form.ends_at ? new Date(this.form.ends_at) : false
              if(this.form.data._ends_at && starts_at > ends_at)
                this.errors.push('Start date cannot be greater than end date')
          }
          if(this.errors.length > 0)
            this.errors.reverse().forEach(error => {
              this.openNotificationWithIcon('error', error)
              sleep(500)
            })
          else
            this.current += 1
        },
        backPage(){
          this.current -= 1
        },
        validateForm(form){
          if(form.data.entitled_product_ids && form.data.entitled_product_ids.length > 0){
            form.data.entitled_product_ids = form.data.entitled_product_ids.map(product => {
              return {
                id: product.id,
                stock_no: product.stock_no,
                reference_code: product.reference_code,
                name: product.name,
                status: product.status,
                product_type: product.product_type,
                barcode: product.barcode,
                unit_code: product.unit_code,
                is_ecomm_product: product.is_ecomm_product,
                account_id: product.account_id,
                integration_id: product.integration_id,
                category_code: product.category_code,
                division_code: product.division_code,
                section_code: product.section_code,
                department_code: product.department_code,
                supplier_code: product.supplier_code,
                brand_code: product.brand_code
              }
           })
          }
          if(form.data.prerequisite_product_ids && form.data.prerequisite_product_ids.length > 0){
            form.data.prerequisite_product_ids = form.data.prerequisite_product_ids.map(product => {
              return {
                id: product.id,
                stock_no: product.stock_no,
                reference_code: product.reference_code,
                name: product.name,
                status: product.status,
                product_type: product.product_type,
                barcode: product.barcode,
                unit_code: product.unit_code,
                is_ecomm_product: product.is_ecomm_product,
                account_id: product.account_id,
                integration_id: product.integration_id,
                category_code: product.category_code,
                division_code: product.division_code,
                section_code: product.section_code,
                department_code: product.department_code,
                supplier_code: product.supplier_code,
                brand_code: product.brand_code
              }
           })
         }
         return form
        },
        async submitPromo(){
          this.doneLoading = true
          let response = {}
          this.form = this.validateForm(this.form)
          try {
            if(this.mode === 'create')
              response = await HTTPRequest.postRequest(this.endPoint + 'sales_channel_promotions', null, this.form)
            else
              response = await HTTPRequest.putRequest(this.endPoint + 'sales_channel_promotions/' + this.form.id, null, this.form)
            if(!response.error && response.id)
              this.$emit('refreshPage', event)
            else
              response.errors.reverse().forEach(error => {
                this.openNotificationWithIcon('error', error)
                sleep(500)
              })
          } catch(error) {
            this.salesChannelAccounts = []
          }
          this.doneLoading = false
        },
        displaySalesChannel: function(salesChannel){
          let promos = []
          switch (salesChannel) {
            case 'online_store':
              promos = [
                // 'automatic_percentage_discount',
                // 'automatic_fixed_amount_discount',
                // 'automatic_buy_get',
                // 'voucher_percentage_discount',
                // 'voucher_fixed_amount_discount',
                // 'voucher_free_shipping',
                // 'voucher_buy_get'
              ].includes(this.form.sales_channel_promo_logic)
              break
            case 'shopify':
              promos = [
                'voucher_percentage_discount',
                'voucher_fixed_amount_discount',
                'voucher_free_shipping',
                'voucher_buy_get'
              ].includes(this.form.sales_channel_promo_logic)
              break
            case 'lazada':
              promos = [
                'automatic_free_shipping',
                // 'automatic_flexi_combo',
                'voucher_percentage_discount',
                'voucher_fixed_amount_discount',
              ].includes(this.form.sales_channel_promo_logic)
              break
            case 'shopee':
              promos = [
                // 'automatic_discounted_amount',
                // 'automatic_bundle_deals',
                // 'automatic_add_on_deals',
                // 'voucher_percentage_discount',
                // 'voucher_fixed_amount_discount',
                // 'voucher_discounted_amount',
              ].includes(this.form.sales_channel_promo_logic)
              break
            default:
              promos = false
              break
          }
          return promos
        },
        getPromotion: async function(){
          this.loading = true
          const response = await HTTPRequest.getRequest(this.endPoint + 'sales_channel_promotions/' + this.promotionId, null)
          this.promotion = response || {}
          this.form = {
            ...this.form,
            ...this.promotion
          }
          Object.keys(this.form.sales_channel_stores).forEach(k =>
            this.form.sales_channel_stores[k] = this.form.sales_channel_stores[k].active
          );
          if(this.form.export_status === 'posted')
            this.form.export_status = 'saved'
          this.form.data.starts_at = this.getDateString(this.form.data.starts_at)
          this.form.data.ends_at = this.form.data.ends_at ? this.getDateString(this.form.data.ends_at) : (new Date('2050-01-01')).toDateString()
          this.loading = false
          this.initial = true
        },
        getDateString: function(date){
          return new Date(date).toString()
        },
        getSalesChannels: async function(){
          try {
            const response = await HTTPRequest.getRequest(this.endPoint + 'sales_channels?statusFilter=A', null)
            this.salesChannelAccounts = response.data || []
          } catch(error) {
            this.salesChannelAccounts = []
          }
        },
        getSalesChannelAccounts: function(code){
          return this.salesChannelAccounts.filter(r => r.sales_channel_type === code)
        },
        availableSalesChannels: function(){
          let salesChannelTypes = this.salesChannelAccounts.map(x => x.sales_channel_type)
          return this.salesChannels.filter(r => salesChannelTypes.includes(r.code))
        },
        validateSalesChannelStores: function(isCreate){
          let arr = Object.fromEntries(Object.entries(this.form.sales_channel_stores).filter(v => v[1]))
          return isCreate ? arr : this.form.sales_channel_stores
        },
        includesStore: function(type){
          let store, include = false
          Object.keys(this.form.sales_channel_stores).forEach(id => {
            store = this.salesChannelAccounts.find(r => `${r.id}` === `${id}`)
            if(this.form.sales_channel_stores[id] && store && store.sales_channel_type === type)
             include = true
          })
          return include
        }
      },
      watch: {
        '$props.promotionId': {
          handler: function () {
            this.resetData()
          }
        },
        '$data.form.application_type': {
          handler: function (after) {
            if(!this.initial)
              this.form.sales_channel_promo_logic = after === 'automatic' ? 'automatic_percentage_discount' : 'voucher_percentage_discount'
          },
          deep: true
        },
        '$data.form.promo_status': {
          handler: function (after) {
            if(after === "inactive")
              this.form.data._ends_at = true
          },
          deep: true
        },
        '$data.form.sales_channel_promo_logic': {
          handler: function () {
            if(!this.initial && this.form.sales_channel_promo_logic !== this.promotion.sales_channel_promo_logic || !this.promotionId)
              this.form.sales_channel_stores = {}
            this.initial = false
          },
          deep: true
        }
      },
      mounted: function() {
        this.getSalesChannels()
        if(this.promotionId)
          this.getPromotion()
      }
    }
</script>
